// src/components/staffing/comparison-utils.js
import { Logger } from '../../utils/logger';
import { CURRENT_DATE } from '../../utils/constants';
import { COLOR_GOOD, COLOR_WARN, COLOR_POOR, TEXT_DISABLED, BRAND_PURPLE } from '../../utils/colors';

const logger = new Logger('comparison-utils');

/**
 * Utility functions for team configuration comparison
 */

/**
 * Calculate the earliest available start date for a team configuration
 * @param {Object} configuration - Team configuration object
 * @returns {Object} - Object with formatted start date string and color
 */
export const calculateEarliestStartDate = (configuration) => {
  if (!configuration || !configuration.team_members) {
    return { text: 'Unknown', color: TEXT_DISABLED };
  }

  const teamMembers = configuration.team_members;

  // Check if we have availability_reason from team scorer (preferred method)
  const hasAvailabilityAnalysis = teamMembers.some(m => m.availability_reason);

  if (hasAvailabilityAnalysis) {
    // Use the backend's availability analysis (compares availability vs role start date)
    let worstCase = null;
    let worstCaseMember = null;

    for (const member of teamMembers) {
      const reason = member.availability_reason;
      if (!reason) continue;

      // Check if this is a delay (not "Available Now")
      if (reason !== 'Available Now' && !reason.includes('Low Utilization')) {
        // Try to extract a date from the reason (e.g., "Available Oct 15")
        const dateMatch = reason.match(/Available\s+([A-Za-z]+\s+\d+)/);
        if (dateMatch) {
          try {
            const availDate = new Date(dateMatch[1] + ', 2024'); // Parse the date
            if (!worstCase || availDate > worstCase) {
              worstCase = availDate;
              worstCaseMember = member;
            }
          } catch (e) {
            // If we can't parse the date, still track this as a delay
            if (!worstCaseMember) {
              worstCaseMember = member;
            }
          }
        } else if (!worstCaseMember) {
          // No date found but it's not "Available Now", so there's some constraint
          worstCaseMember = member;
        }
      }
    }

    // Return results based on worst case
    if (worstCaseMember) {
      let color = COLOR_WARN; // default
      let text = worstCaseMember.availability_reason;

      // Determine color based on how far out
      if (worstCase) {
        const diffTime = worstCase.getTime() - CURRENT_DATE.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays <= 28) {
          color = COLOR_WARN; // within 4 weeks
        } else {
          color = COLOR_POOR; // more than 4 weeks
        }

        text = `${text} (${worstCaseMember.employee_name})`;
      } else {
        // Have a constraint but couldn't parse date
        text = `${text} (${worstCaseMember.employee_name})`;
      }

      return { text, color };
    } else {
      return { text: 'All roles on time ✓', color: COLOR_GOOD };
    }
  }

  // Fallback: Original logic for legacy configurations without availability_reason
  let latestAvailableDate = null;

  for (const member of teamMembers) {
    const nextAvailable = member.next_available_date || member.available_date || member.available_from;

    if (nextAvailable) {
      try {
        const availableDate = new Date(nextAvailable);
        if (!isNaN(availableDate.getTime())) {
          if (availableDate > CURRENT_DATE) {
            if (!latestAvailableDate || availableDate > latestAvailableDate) {
              latestAvailableDate = availableDate;
            }
          }
        }
      } catch (error) {
        logger.warn('Error parsing availability date:', nextAvailable, error);
      }
    } else {
      const availabilityTier = member.availability_tier;

      if (availabilityTier === 2) {
        const assumedDate = new Date(CURRENT_DATE);
        assumedDate.setDate(assumedDate.getDate() + 14);
        if (!latestAvailableDate || assumedDate > latestAvailableDate) {
          latestAvailableDate = assumedDate;
        }
      } else if (availabilityTier === 3) {
        const assumedDate = new Date(CURRENT_DATE);
        assumedDate.setDate(assumedDate.getDate() + 28);
        if (!latestAvailableDate || assumedDate > latestAvailableDate) {
          latestAvailableDate = assumedDate;
        }
      }
    }
  }

  let color = COLOR_GOOD;
  let text = 'Available Now';

  if (latestAvailableDate) {
    const diffTime = latestAvailableDate.getTime() - CURRENT_DATE.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    text = formatDate(latestAvailableDate);

    if (diffDays <= 0) {
      color = COLOR_GOOD;
      text = 'Available Now';
    } else if (diffDays <= 28) {
      color = COLOR_WARN;
    } else {
      color = COLOR_POOR;
    }
  }

  return { text, color };
};

/**
 * Format date for display
 * @param {Date} date - Date object
 * @returns {string} - Formatted date string
 */
export const formatDate = (date) => {
  const diffTime = date.getTime() - CURRENT_DATE.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) {
    return 'Available Now';
  } else if (diffDays <= 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'}`;
  } else if (diffDays <= 30) {
    const weeks = Math.ceil(diffDays / 7);
    return `${weeks} week${weeks === 1 ? '' : 's'}`;
  } else {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== CURRENT_DATE.getFullYear() ? 'numeric' : undefined
    });
  }
};

/**
 * Calculate enhanced metrics for a configuration
 * @param {Object} configuration - Team configuration object
 * @returns {Object} - Enhanced metrics object
 */
export const calculateEnhancedMetrics = (configuration) => {
  if (!configuration) {
    return {};
  }

  // Calculate actual cost and revenue based on team members
  const calculateCostAndRevenue = (config) => {
    if (!config.team_members || config.team_members.length === 0) {
      return {
        cost: config.comparison_metrics?.estimated_cost || 0,
        revenue: config.comparison_metrics?.estimated_revenue || 0,
        margin: 0
      };
    }

    // Default project duration in weeks (can be overridden by stored duration)
    const projectDurationWeeks = config.comparison_metrics?.estimated_duration_weeks || 12;
    const hoursPerWeek = 40;
    const totalHours = projectDurationWeeks * hoursPerWeek;

    let totalCost = 0;
    let totalRevenue = 0;

    // Calculate cost and revenue based on team member rates
    config.team_members.forEach(member => {
      // Calculate hours for this team member (assume full project duration unless specified)
      const memberHours = member.allocated_hours || totalHours;

      // Calculate cost (employee cost rate)
      let memberCostRate = member.employee_cost_rate;
      
      if (!memberCostRate && member.hourly_rate) {
        // Estimate cost rate as 70% of billing rate
        memberCostRate = member.hourly_rate * 0.7;
      }
      
      if (!memberCostRate) {
        // Fallback to default cost rates based on role
        const defaultCostRates = {
          'Senior Developer': 140,
          'Developer': 120,
          'Junior Developer': 100,
          'Tech Lead': 160,
          'Project Manager': 130,
          'Designer': 110,
          'QA Engineer': 100,
          'DevOps Engineer': 150
        };
        memberCostRate = defaultCostRates[member.role] || 120;
      }

      // Calculate revenue (billing rate)
      let memberBillingRate = member.hourly_rate;
      
      if (!memberBillingRate) {
        // Fallback to default billing rates based on role
        const defaultBillingRates = {
          'Senior Developer': 200,
          'Developer': 170,
          'Junior Developer': 140,
          'Tech Lead': 230,
          'Project Manager': 185,
          'Designer': 160,
          'QA Engineer': 140,
          'DevOps Engineer': 215
        };
        memberBillingRate = defaultBillingRates[member.role] || 170;
      }

      totalCost += memberCostRate * memberHours;
      totalRevenue += memberBillingRate * memberHours;
    });

    const margin = totalRevenue > 0 ? ((totalRevenue - totalCost) / totalRevenue) * 100 : 0;

    return {
      cost: totalCost,
      revenue: totalRevenue,
      margin: margin
    };
  };

  const costAndRevenue = calculateCostAndRevenue(configuration);

  // Prefer live team_analysis_data.scores; fall back to stored optimization_analysis
  const liveScores = configuration.team_analysis_data?.scores;
  const liveFinancial = configuration.team_analysis_data?.financialView;

  const overallScore = liveScores?.overall ?? configuration.optimization_analysis?.overall_score ?? 0;
  const technicalScore = liveScores?.technical ?? configuration.comparison_metrics?.skill_coverage_percentage ?? 0;
  const chemistryScore = liveScores?.chemistry ?? configuration.comparison_metrics?.team_chemistry_score ?? 0;
  const availabilityScore = liveScores?.availability ?? 0;
  const experienceScore = liveScores?.experience ?? configuration.optimization_analysis?.experience_balance ?? 0;

  // Prefer blended margin from live financial data over recomputed estimate
  const liveMarginPct = liveFinancial?.blendedMarginPct ? parseFloat(liveFinancial.blendedMarginPct) : null;
  const finalMargin = liveMarginPct ?? costAndRevenue.margin;

  const baseMetrics = {
    overall_score: overallScore,
    success_rate: configuration.optimization_analysis?.predicted_success_rate || 0,
    cost: costAndRevenue.cost,
    revenue: costAndRevenue.revenue,
    margin: finalMargin,
    duration: configuration.comparison_metrics?.estimated_duration_weeks || 12,
    risk_level: configuration.optimization_analysis?.risk_assessment || 'medium',
    team_size: configuration.team_members?.length || 0
  };

  // Enhanced metrics
  const startDateInfo = calculateEarliestStartDate(configuration);
  const enhancedMetrics = {
    ...baseMetrics,
    technical_coverage: technicalScore,
    team_chemistry: chemistryScore,
    availability: availabilityScore,
    experience_balance: experienceScore,
    collaboration_score: configuration.optimization_analysis?.collaboration_score || 0,
    disc_compatibility: configuration.optimization_analysis?.disc_compatibility || 0,
    strengths_synergy: configuration.optimization_analysis?.strengths_synergy || 0,
    start_date: startDateInfo
  };

  return enhancedMetrics;
};

/**
 * Generate comparison data for multiple configurations
 * @param {Array} configurations - Array of configuration objects
 * @returns {Object} - Comparison data object
 */
export const generateComparisonData = (configurations) => {
  if (!configurations || configurations.length === 0) {
    return { configurations: [], metrics: [] };
  }

  const validConfigs = configurations.filter(config => config !== null);
  
  const comparisonData = {
    configurations: validConfigs.map(config => ({
      ...config,
      enhanced_metrics: calculateEnhancedMetrics(config)
    })),
    metrics: [
      {
        key: 'overall_score',
        label: 'Overall Score',
        type: 'overall_score',
        tooltip: 'Comprehensive team optimization score based on multiple factors',
        maxValue: 10,
        unit: ''
      },
      {
        key: 'success_rate',
        label: 'Expected Success Rate',
        type: 'success_rate',
        tooltip: 'Predicted probability of project success with this team',
        maxValue: 1,
        unit: ''
      },
      {
        key: 'start_date',
        label: 'Available Start',
        type: 'start_date',
        tooltip: 'Earliest date when all team members can begin work',
        showProgress: false
      },
      {
        key: 'cost',
        label: 'Estimated Cost / Margin',
        type: 'cost_margin',
        tooltip: 'Total estimated cost and profit margin for the project duration',
        maxValue: Math.max(...validConfigs.map(c => calculateEnhancedMetrics(c).cost)) * 1.2,
        unit: ''
      },
      {
        key: 'technical_coverage',
        label: 'Tech Coverage',
        type: 'score',
        tooltip: 'Technical skills coverage score (0–10)',
        maxValue: 10,
        unit: ''
      },
      {
        key: 'team_chemistry',
        label: 'Team Chemistry',
        type: 'team_chemistry',
        tooltip: 'Predicted collaboration effectiveness based on DISC profiles and history',
        maxValue: 10,
        unit: ''
      },
      {
        key: 'availability',
        label: 'Availability',
        type: 'availability',
        tooltip: 'Team availability score based on current staffing commitments',
        maxValue: 10,
        unit: ''
      },
      {
        key: 'experience_balance',
        label: 'Experience',
        type: 'experience_balance',
        tooltip: 'Blended experience score across client, industry, and collaboration history',
        maxValue: 10,
        unit: ''
      }
    ]
  };

  return comparisonData;
};

/**
 * Export comparison data to CSV format
 * @param {Object} comparisonData - Comparison data object
 * @returns {string} - CSV formatted string
 */
export const exportToCSV = (comparisonData) => {
  if (!comparisonData.configurations || comparisonData.configurations.length === 0) {
    return '';
  }

  const headers = ['Metric', ...comparisonData.configurations.map(config => config.configuration_name)];
  const rows = [headers];

  comparisonData.metrics.forEach(metric => {
    const row = [metric.label];
    comparisonData.configurations.forEach(config => {
      const value = config.enhanced_metrics[metric.key];
      let formattedValue = value;
      
      if (metric.type === 'success_rate') {
        formattedValue = `${Math.round(value * 100)}%`;
      } else if (metric.type === 'cost') {
        formattedValue = `$${Math.round(value / 1000)}K`;
      } else if (metric.type === 'duration') {
        formattedValue = `${value} weeks`;
      } else if (metric.type === 'technical_coverage') {
        formattedValue = `${Math.round(value)}%`;
      } else if (typeof value === 'number') {
        formattedValue = value.toFixed(1);
      }
      
      row.push(formattedValue);
    });
    rows.push(row);
  });

  return rows.map(row => row.join(',')).join('\n');
};

/**
 * Download CSV file
 * @param {string} csvContent - CSV content string
 * @param {string} filename - Filename for download
 */
export const downloadCSV = (csvContent, filename = 'team-configuration-comparison.csv') => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

/**
 * Generate print-friendly HTML for comparison
 * @param {Object} comparisonData - Comparison data object
 * @returns {string} - HTML string for printing
 */
export const generatePrintHTML = (comparisonData) => {
  if (!comparisonData.configurations || comparisonData.configurations.length === 0) {
    return '<p>No configurations to compare</p>';
  }

  const timestamp = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  let html = `
    <html>
      <head>
        <title>Team Configuration Comparison</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: ${BRAND_PURPLE}; border-bottom: 2px solid ${BRAND_PURPLE}; padding-bottom: 10px; }
          h2 { color: #333; margin-top: 30px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
          th { background-color: #f5f5f5; font-weight: bold; }
          .metric-label { font-weight: bold; background-color: #fafafa; }
          .timestamp { color: #666; font-size: 0.9em; margin-bottom: 20px; }
          .config-header { background-color: #e3f2fd; }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <h1>Team Configuration Comparison</h1>
        <div class="timestamp">Generated on ${timestamp}</div>
        
        <table>
          <thead>
            <tr>
              <th>Metric</th>
              ${comparisonData.configurations.map(config => 
                `<th class="config-header">${config.configuration_name}</th>`
              ).join('')}
            </tr>
          </thead>
          <tbody>
  `;

  comparisonData.metrics.forEach(metric => {
    html += `<tr><td class="metric-label">${metric.label}</td>`;
    
    comparisonData.configurations.forEach(config => {
      const value = config.enhanced_metrics[metric.key];
      let formattedValue = value;
      
      if (metric.type === 'success_rate') {
        formattedValue = `${Math.round(value * 100)}%`;
      } else if (metric.type === 'cost') {
        formattedValue = `$${Math.round(value / 1000)}K`;
      } else if (metric.type === 'duration') {
        formattedValue = `${value} weeks`;
      } else if (metric.type === 'technical_coverage') {
        formattedValue = `${Math.round(value)}%`;
      } else if (typeof value === 'number') {
        formattedValue = value.toFixed(1);
      }
      
      html += `<td>${formattedValue}</td>`;
    });
    
    html += '</tr>';
  });

  html += `
          </tbody>
        </table>
      </body>
    </html>
  `;

  return html;
};

/**
 * Print comparison data
 * @param {Object} comparisonData - Comparison data object
 */
export const printComparison = (comparisonData) => {
  const printWindow = window.open('', '_blank');
  const htmlContent = generatePrintHTML(comparisonData);
  
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  printWindow.focus();
  
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 250);
};
